import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import AISuggestionSection from '../AISuggestionSection'
import { useTranslation } from 'react-i18next'
import AI from '../icons/AI'
import { Grid } from '@material-ui/core'
import LinkButton from 'components/common/buttons/LinkButton'
import SendMessage from '../icons/SendMessage'
import useSendChatMessage from 'hooks/useSendChatMessage'
import AISuggestedImages from './AISuggestedImages'
import useAISuggestedImages from './useAISuggestedImages'
import AISuggestedMessage from './AISuggestedMessage'
import { useGetNextMessageQuery } from 'store/modules/ScanTagging'
import { useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'

const useStyles = makeStyles(theme => ({
  sendMessageIcon: {
    paddingRight: '0px !important'
  }
}))

const AISuggestedActions = ({ scanId }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { patient } = useSelector(state => state.patientsReducer)
  const { doctor } = useSelector(state => state.profileReducer)

  const { sendMessage } = useSendChatMessage()
  const { data, refetch, isFetching } = useGetNextMessageQuery(
    { patientId: patient.id, scanId, hiDoctorId: doctor.id },
    { skip: !scanId || !patient.id || !doctor.id, refetchOnMountOrArgChange: true }
  )
  const { oralHygieneRecommendationImages } = useAISuggestedImages({ scanId })

  const [selectedImagesIndex, setSelectedImagesIndex] = useState([])
  const [text, setText] = useState('')
  const [wasMessageSent, setWasMessageSent] = useState(false)
  const [wasAIMessageEdited, setWasAIMessageEdited] = useState(false)

  const handleImageSelected = useCallback(index => {
    setSelectedImagesIndex(prev => {
      const newSelected = [...prev]
      if (newSelected.includes(index)) {
        newSelected.splice(newSelected.indexOf(index), 1)
      } else {
        newSelected.push(index)
      }
      return newSelected
    })
  }, [])

  const handleSendMessage = useCallback(() => {
    if (selectedImagesIndex.length > 0) {
      trackEvent('Chat - message sent', {
        trigger: 'AIforHI',
        wasAIMessageEdited,
        messageType: 'imageV2',
        patientId: patient?.id
      })

      // Sending each selected image as a separate message
      // with the first image containing the text
      selectedImagesIndex.forEach(index => {
        sendMessage({
          type: 'imageV2',
          content: JSON.stringify({
            s3Object: oralHygieneRecommendationImages[index],
            text: index === 0 ? text : ''
          }),
          additionalFields: {
            successMessage: t('pages.patients.selectedPatient.chat.gi.AIMessagePatient.successMessage')
          }
        })
      })
    } else {
      trackEvent('Chat - message sent', {
        trigger: 'AIforHI',
        wasAIMessageEdited,
        messageType: 'text',
        patientId: patient?.id
      })

      sendMessage({
        type: 'text',
        content: text,
        additionalFields: {
          successMessage: t('pages.patients.selectedPatient.chat.gi.AIMessagePatient.successMessage')
        }
      })
    }
    setWasMessageSent(true)
  }, [oralHygieneRecommendationImages, patient?.id, selectedImagesIndex, sendMessage, t, text, wasAIMessageEdited])

  const handleRegenerateMessage = useCallback(async () => {
    trackEvent('AI for HI - Regenerate AI message clicked')
    await refetch()
  }, [refetch])

  useEffect(() => {
    if (!!data?.nextMessage) {
      setWasAIMessageEdited(false)
      setText(data?.nextMessage)
    }
  }, [data?.nextMessage])

  return (
    <>
      <AISuggestionSection
        shouldDisplayBorder
        icon={<AI />}
        title={t('pages.patients.selectedPatient.chat.gi.AIMessagePatient.title')}
        wasActionCompleted={wasMessageSent}
        completedTitle={t('pages.patients.selectedPatient.chat.gi.AIMessagePatient.completedTitle')}
      >
        <Grid container direction="column" spacing={1}>
          <Grid item style={{ position: 'relative' }}>
            <AISuggestedMessage
              text={text}
              setText={setText}
              wasAIMessageEdited={wasAIMessageEdited}
              setWasAIMessageEdited={setWasAIMessageEdited}
              isLoading={isFetching}
              onRegenerateMessage={handleRegenerateMessage}
            />
          </Grid>
          <Grid item>
            <AISuggestedImages
              scanId={scanId}
              oralHygieneRecommendationImages={oralHygieneRecommendationImages}
              selectedImagesIndex={selectedImagesIndex}
              handleImageSelected={handleImageSelected}
            />
          </Grid>
          <Grid item>
            <LinkButton
              bold
              keepTextWhileLoading
              fontSize={14}
              disabled={!text.trim()}
              label={t('pages.patients.selectedPatient.chat.gi.AIMessagePatient.sendMessage')}
              icon={<SendMessage />}
              onClick={handleSendMessage}
              iconContainerClassName={classes.sendMessageIcon}
            />
          </Grid>
        </Grid>
      </AISuggestionSection>
    </>
  )
}

export default AISuggestedActions
